/** Currency switcher — INR/USD/EUR/GBP/AUD/CAD dropdown, blueprint-style. */
import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "../../contexts/CurrencyContext";
import { cn } from "../../lib/cn";

export function CurrencySwitcher({ className }) {
  const { code, setCode, currencies } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Select currency"
        data-testid="currency-switcher"
        className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border border-line-2 bg-surface text-text text-[13px] font-semibold hover:border-mauve hover:bg-surface-2 transition-colors"
      >
        <span data-testid="currency-label">{code}</span>
        <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[180px] rounded-2xl border border-line-2 bg-glass-2 backdrop-blur-xl shadow-float p-1.5 z-50"
          data-testid="currency-menu"
        >
          {Object.entries(currencies).map(([key, val]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setCode(key);
                setOpen(false);
              }}
              data-testid={`currency-option-${key}`}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 rounded-xl text-[13px] hover:bg-surface-2 transition-colors",
                key === code && "bg-surface-2 text-mauve"
              )}
            >
              <span>
                <b className="font-semibold">{key}</b>
              </span>
              <span className="font-mono text-text-dim">{val.sym}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
